import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useToast, getAdminSessionToken, API } from "../_shared";
import { settingService } from "../../../services/settingService";
import { getEmptySettings } from "./adminSettingsDefaults";
import { multipartUploadToS3, buildUniqueUploadKey, SINGLE_UPLOAD_MAX_BYTES } from "../../../lib/multipartUpload";
import { SETTINGS_SECTION_IDS } from "./adminSettingsConstants";

const AdminSettingsContext = createContext(null);

function useAdminSettingsState() {
  const location = useLocation();
  const navigate = useNavigate();
  const activeSection = useMemo(() => {
    const seg = (location.pathname || "").replace(/.*\/settings\//, "").split("/")[0] || "global";
    return SETTINGS_SECTION_IDS.includes(seg) ? seg : "global";
  }, [location.pathname]);

  const { push: toast, ToastPortal } = useToast();
  const [loading, setLoading] = useState(true);
  // Lỗi tải cài đặt: chặn Lưu để không ghi đè cấu hình thật bằng dữ liệu rỗng
  const [loadError, setLoadError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [data, setData] = useState(getEmptySettings());
  const [uploadingField, setUploadingField] = useState(null);

  // Modals
  const [showAssetPreview, setShowAssetPreview] = useState(null); // field: logo|favicon|ogImage|background
  const [showImageEdit, setShowImageEdit] = useState(null); // { field, file, title, shape }
  const [showMediaPicker, setShowMediaPicker] = useState(null);
  const [showWebsitePreview, setShowWebsitePreview] = useState(false);
  const [showFooterPreview, setShowFooterPreview] = useState(false);
  const [showPagePreview, setShowPagePreview] = useState(null);

  const previewData = {
    ...data,
  };

  useEffect(() => {
    const seg = (location.pathname || "").replace(/.*\/settings\//, "").split("/")[0] || "";
    if (seg && !SETTINGS_SECTION_IDS.includes(seg)) {
      navigate("/admin/settings/global", { replace: true });
    }
  }, [location.pathname, navigate]);

  // Tải cài đặt (lúc mở trang và sau khi khôi phục phiên bản cũ)
  const loadSettings = useCallback(async () => {
      setLoadError(null);
      try {
        const res = await settingService.getSettings();
        if (!res || typeof res !== "object") throw new Error("Dữ liệu cài đặt không hợp lệ");
        // Merge with default structure to ensure all fields exist
        const defaultSettings = getEmptySettings();
        const mergedData = {
          ...defaultSettings,
          ...res,
          seo: { ...defaultSettings.seo, ...res.seo },
          aboutPage: { ...defaultSettings.aboutPage, ...res.aboutPage },
        };
        setData(mergedData);
      } catch (error) {
        console.error("Failed to load settings:", error);
        setLoadError(error?.message || "Không rõ nguyên nhân");
        toast(`Không thể tải cài đặt: ${error.message}`, "error", { duration: 4000 });
      } finally {
        setLoading(false);
      }
  }, [toast]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  // Bấm "Thử lại" sau khi tải lỗi: hiện lại trạng thái đang tải
  const retryLoadSettings = useCallback(() => {
    setLoading(true);
    loadSettings();
  }, [loadSettings]);

  // Save settings
  const handleSave = useCallback(async () => {
    if (loading || loadError) {
      toast("Chưa tải được cài đặt hiện tại — không thể lưu. Vui lòng bấm \"Thử lại\".", "error", { duration: 4000 });
      return;
    }
    setSaving(true);
    try {
      const token = getAdminSessionToken();
      const res = await settingService.updateSettings(token, data);
      toast(res?.version ? `Đã lưu cài đặt (phiên bản ${res.version})` : "Cài đặt đã được lưu thành công!", "success", { duration: 3000 });
    } catch (error) {
      console.error("Save error:", error);
      toast(`Lỗi khi lưu: ${error.message}`, "error", { duration: 4000 });
    } finally {
      setSaving(false);
    }
  }, [data, toast, loading, loadError]);

  // Asset upload handler
  const handleAssetUpload = async (field, file) => {
    setUploadingField(field);
    try {
      const token = getAdminSessionToken();
      const result = await settingService.uploadAsset(token, field, file);
      
      // Update the appropriate field in data
      // Handle asset fields (logo, favicon, etc.)
      setData(prev => ({ ...prev, [field]: result.data }));
      
      toast("Upload thành công!", "success", { duration: 3000 });

      // Show "where it appears" preview right after upload
      if (field === "logo" || field === "favicon" || field === "ogImage" || field === "background") {
        setShowAssetPreview(field);
      }
    } catch (error) {
      console.error("Upload error:", error);
      toast(`Lỗi upload: ${error.message}`, "error", { duration: 4000 });
    } finally {
      setUploadingField(null);
    }
  };

  const updateField = useCallback((path, value) => {
    setData((prev) => {
      const newData = { ...prev };
      const keys = path.split(".");
      let current = newData;

      for (let i = 0; i < keys.length - 1; i++) {
        if (!current[keys[i]]) current[keys[i]] = {};
        current[keys[i]] = { ...current[keys[i]] };
        current = current[keys[i]];
      }

      current[keys[keys.length - 1]] = value;
      return newData;
    });
  }, []);

  // Helper to add array item
  const addArrayItem = (path, defaultItem) => {
    setData(prev => {
      const newData = { ...prev };
      const keys = path.split('.');
      let current = newData;
      
      for (let i = 0; i < keys.length - 1; i++) {
        if (!current[keys[i]]) current[keys[i]] = {};
        current[keys[i]] = { ...current[keys[i]] };
        current = current[keys[i]];
      }
      
      const arrayKey = keys[keys.length - 1];
      if (!Array.isArray(current[arrayKey])) {
        current[arrayKey] = [];
      }
      current[arrayKey] = [...current[arrayKey], { ...defaultItem }];
      return newData;
    });
  };

  // Helper to remove array item
  const removeArrayItem = (path, index) => {
    setData(prev => {
      const newData = { ...prev };
      const keys = path.split('.');
      let current = newData;
      
      for (let i = 0; i < keys.length - 1; i++) {
        if (!current[keys[i]]) current[keys[i]] = {};
        current[keys[i]] = { ...current[keys[i]] };
        current = current[keys[i]];
      }
      
      const arrayKey = keys[keys.length - 1];
      if (Array.isArray(current[arrayKey])) {
        current[arrayKey] = current[arrayKey].filter((_, i) => i !== index);
      }
      return newData;
    });
  };

  // Helper to update array item
  const updateArrayItem = (path, index, field, value) => {
    setData(prev => {
      const newData = { ...prev };
      const keys = path.split('.');
      let current = newData;
      
      for (let i = 0; i < keys.length - 1; i++) {
        if (!current[keys[i]]) current[keys[i]] = {};
        current[keys[i]] = { ...current[keys[i]] };
        current = current[keys[i]];
      }
      
      const arrayKey = keys[keys.length - 1];
      if (Array.isArray(current[arrayKey]) && current[arrayKey][index]) {
        current[arrayKey] = [...current[arrayKey]];
        current[arrayKey][index] = { ...current[arrayKey][index], [field]: value };
      }
      return newData;
    });
  };

  const uploadLocalImageToMedia = useCallback(
    async ({ path, file, prefix = "site/" }) => {
      const token = getAdminSessionToken();
      if (!token) throw new Error("Chưa đăng nhập admin");
      if (!file) return;

      let a;
      if (Number(file.size || 0) > SINGLE_UPLOAD_MAX_BYTES) {
        // Upload 1 request bị giới hạn ~4.5MB → tệp lớn đi multipart (key duy nhất)
        a = await multipartUploadToS3({
          apiBase: API,
          token,
          key: buildUniqueUploadKey(prefix, file),
          file,
          contentType: file.type || "application/octet-stream",
        });
      } else {
        const fd = new FormData();
        fd.append("file", file);

        const qs = new URLSearchParams();
        qs.set("prefix", prefix);

        const res = await fetch(`${API}/media/upload?${qs.toString()}`, {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: fd,
        });
        const out = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(out?.message || "Upload thất bại");
        a = out?.data || {};
      }
      a = a || {};
      updateField(path, {
        key: a.key || "",
        url: a.url || "",
        mimeType: a.mimeType || file.type || "",
        size: Number(a.size || file.size || 0),
      });
      toast("Đã tải ảnh lên Media", "success", { duration: 2500 });
    },
    [toast, updateField]
  );

  return {
    reloadSettings: loadSettings,
    retryLoadSettings,
    toast,
    ToastPortal,
    location,
    navigate,
    activeSection,
    loading,
    loadError,
    saving,
    data,
    setData,
    uploadingField,
    previewData,
    uploadLocalImageToMedia,
    handleSave,
    handleAssetUpload,
    updateField,
    addArrayItem,
    removeArrayItem,
    updateArrayItem,
    setShowAssetPreview,
    setShowImageEdit,
    setShowMediaPicker,
    setShowWebsitePreview,
    setShowFooterPreview,
    setShowPagePreview,
    showAssetPreview,
    showImageEdit,
    showMediaPicker,
    showWebsitePreview,
    showFooterPreview,
    showPagePreview,
  };
}

export function AdminSettingsProvider({ children }) {
  const value = useAdminSettingsState();
  return (
    <AdminSettingsContext.Provider value={value}>
      {children}
    </AdminSettingsContext.Provider>
  );
}

export function useAdminSettings() {
  const ctx = useContext(AdminSettingsContext);
  if (!ctx) {
    throw new Error("useAdminSettings phải dùng bên trong AdminSettingsProvider");
  }
  return ctx;
}
